/**
 * Calculate reading time for blog posts
 * Based on average reading speed of 200 words per minute
 */

const WORDS_PER_MINUTE = 200;

/**
 * Calculate reading time from markdown content
 * @param {string} content - The markdown content
 * @returns {number} - Reading time in minutes
 */
export const calculateReadingTime = (content) => {
  if (!content || typeof content !== 'string') {
    return 0;
  }

  // Remove markdown syntax and HTML tags
  const cleanText = content
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, '')
    // Remove inline code
    .replace(/`[^`]*`/g, '')
    // Remove markdown links
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    // Remove markdown headers
    .replace(/^#{1,6}\s+/gm, '')
    // Remove markdown bold/italic
    .replace(/\*\*([^*]*)\*\*/g, '$1')
    .replace(/\*([^*]*)\*/g, '$1')
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove extra whitespace
    .replace(/\s+/g, ' ')
    .trim();

  // Count words
  const wordCount = cleanText.split(' ').filter(word => word.length > 0).length;
  
  // Calculate reading time in minutes
  const readingTime = Math.ceil(wordCount / WORDS_PER_MINUTE);
  
  return Math.max(1, readingTime); // Minimum 1 minute
};

/**
 * Format reading time for display
 * @param {number} minutes - Reading time in minutes
 * @returns {string} - Formatted reading time
 */
export const formatReadingTime = (minutes) => {
  if (minutes === 1) {
    return '1 min read';
  }
  return `${minutes} min read`;
};

/**
 * Get word count from content
 * @param {string} content - The markdown content
 * @returns {number} - Word count
 */
export const getWordCount = (content) => {
  if (!content || typeof content !== 'string') {
    return 0;
  }

  const cleanText = content
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`[^`]*`/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]*)\*\*/g, '$1')
    .replace(/\*([^*]*)\*/g, '$1')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return cleanText.split(' ').filter(word => word.length > 0).length;
};
